import { memo } from 'react'

import Loader from 'app/common/components/Loader'
import EmptyReportCSS from 'app/components/common/reports/EmptyReportCSS'
import HistoryLink, { reportTypes } from 'app/components/common/reports/history/HistoryLink'
import { useCurrencySign } from 'app/common/components-next/currency'
import { RowLayoutContent } from 'app/common/layouts/RowLayout'
import { StatusMessage } from 'app/common/templates-next/page-template'
import { ReportSection } from 'app/common/templates-next/report-template'

import HeaderBar from './HeaderBar'
import Schools from './Schools'
import Groups from './Groups'
import Sales from './Sales'
import Items, { ItemsSpecial } from './Items'
import useReportData, { byPaymentMethodDateFormat } from './useReportData'
import useDisplaySettings from './useDisplaySettings'
import { useFiltersSpecialFlags } from './useFiltersSpecialFlags'
import { ProductTypes } from './ProductTypes/product-types'
import type { PaymentMethodsDto, ProductDataDto, ProductDataTotalDto } from './ProductTypes/types'
import { SummaryByPaymentMethod, ResultByPaymentMethod } from './byPaymentMethod'

import type {
  Summary as SummaryType,
  School,
  Group,
  Purchase,
  Item,
  Complex,
  Daily,
  Monthly,
  DailyTotals,
  MonthlyTotals,
} from './types'

import Summary, {
  SummarySpecial,
  SummarySpecialCustom,
  SummarySpecialVisuallySplitted,
} from './Summary'

import styles from './Report.scss'

type Props = {
  reportId: number | null
}

type ReportData = Partial<{
  summary: SummaryType
  totals: Array<DailyTotals> | Array<MonthlyTotals>
  schools: Array<School<Daily>> | Array<School<Monthly>>
  groups: Array<Group<Daily>> | Array<Group<Monthly>>
  purchases: Array<Purchase>
  items: Array<Item>
  complexes: Array<Complex>
  details_group: string | null
  product_types: ProductDataDto
  split_by_days: boolean
  grand_total: ProductDataTotalDto
  payment_methods: PaymentMethodsDto
}>

const Report = memo<Props>(({ reportId }: Props) => {
  const currency = useCurrencySign()

  const [data, loading] = useReportData(reportId)

  const reportData = (data ?? {}) as ReportData

  const {
    summary,
    totals,
    schools,
    groups,
    purchases,
    items,
    complexes,
    details_group: detailsGroup,
    product_types: productTypes,
    split_by_days: splitByDays = false,
    grand_total: grandTotal,
    payment_methods: paymentMethods,
  } = reportData

  const nothing = !(summary || schools || groups || purchases || items || complexes || productTypes)

  const displaySettings = useDisplaySettings(!loading)
  const spec = useFiltersSpecialFlags()

  if (!reportId) {
    return (
      <EmptyReportCSS
        stickyNumber={2}
        headerBar={<HistoryLink reportType={reportTypes.REPORT_BALANCES} />}
      />
    )
  }

  return (
    <ReportSection
      header="Отчет «Продажи»"
      headerBar={
        <HeaderBar
          reportId={reportId}
          hideDownload={!displaySettings.details || spec.datailsNotAvailable}
        />
      }
      fullscreenEnabled={displaySettings.details}
      skipPadding
    >
      {loading ? (
        <Loader />
      ) : (
        <>
          {/*================================ SUMMARY ===================================*/}
          {/* группировка по способу расчета */}
          {summary &&
            displaySettings.byPaymentMethodForMultipleSchools &&
            data?.details_group === 'by_payment_method' &&
            Array.isArray(summary) && (
              <SummaryByPaymentMethod
                summary={data.summary}
                currency={currency}
                itemClassName={styles.item}
              />
            )}
          {/* группировка по стоимости продукции и комплектам */}
          {!displaySettings.byGroupComplexes &&
            !displaySettings.byCampComplexes &&
            !displaySettings.byProductType &&
            !displaySettings.byPaymentMethodForMultipleSchools &&
            summary && <Summary summary={summary} currency={currency} />}
          {displaySettings.byGroupComplexes && summary && !Array.isArray(summary) && (
            <SummarySpecial summary={summary} currency={currency} excludeOVZ={spec.excludeOVZ} />
          )}
          {summary &&
            !displaySettings.byProductType &&
            !displaySettings.byPaymentMethodForMultipleSchools &&
            Array.isArray(summary) && (
              <SummarySpecialCustom
                summary={summary}
                currency={currency}
                excludeOVZ={spec.excludeOVZ}
                itemClassName={styles.item}
              />
            )}
          {displaySettings.byCampComplexes && summary && Array.isArray(summary) && (
            <SummarySpecialCustom
              summary={summary}
              currency={currency}
              excludeOVZ={spec.excludeOVZ}
              showCount
            />
          )}
          {/* типу продукции и комплектам */}
          {summary && displaySettings.byProductType && Array.isArray(summary) && (
            <SummarySpecialVisuallySplitted
              summary={summary}
              currency={currency}
              itemClassName={styles.item}
            />
          )}
          {/*================================ DETAILS ===================================*/}
          {/* группировка по способу расчета */}
          {displaySettings.byPaymentMethodForMultipleSchools &&
            data?.details_group === 'by_payment_method' && (
              <ResultByPaymentMethod
                data={data}
                dateFormat={byPaymentMethodDateFormat}
                splitByDays={splitByDays}
              />
            )}
          {/* Без группировки */}
          {!displaySettings.byGroupComplexes &&
            !displaySettings.byCampComplexes &&
            schools &&
            totals && <Schools schools={schools} totals={totals} />}
          {/* группировка по типам пользователя и классам */}
          {groups && totals && <Groups groups={groups} totals={totals} />}
          {/* группировка по продажам */}
          {purchases && <Sales purchases={purchases} displaySettings={displaySettings} />}
          {/* группировка по продукции и комплектам */}
          {/* группировка по стоимости продукции и комплектам */}
          {!displaySettings.byGroupComplexes &&
            !displaySettings.byCampComplexes &&
            (items || complexes) && (
              <Items
                items={items}
                complexes={complexes}
                skipPortion={detailsGroup === 'by_items_and_complexes_and_cost'}
              />
            )}
          {/* группировка по комплектам (групповой талон) */}
          {(displaySettings.byGroupComplexes || displaySettings.byCampComplexes) && complexes && (
            <ItemsSpecial complexes={complexes} />
          )}
          {/* тип продукции и комплектам */}
          {displaySettings.byProductType && productTypes && paymentMethods && grandTotal && (
            <ProductTypes
              splitByDays={splitByDays}
              productTypes={productTypes}
              paymentMethods={paymentMethods}
              total={grandTotal}
            />
          )}
          {spec.datailsNotAvailable && (
            <RowLayoutContent borderTop paddingfree>
              <StatusMessage className={styles.empty} error>
                {'Детализация недоступна'}
              </StatusMessage>
            </RowLayoutContent>
          )}
          {nothing && !spec.datailsNotAvailable && (
            <RowLayoutContent borderTop paddingfree>
              <StatusMessage className={styles.empty}>{'Данные отсутствуют'}</StatusMessage>
            </RowLayoutContent>
          )}
        </>
      )}
    </ReportSection>
  )
})

export default Report
